// @flow
import { emptyCommonErrors } from '../redux/ducks/advertisementTemplate/reducers'
import type {
  AdvertisementTemplateErrors,
  CommonErrors,
  CommonErrorsInfo,
  MediaErrors
} from '../redux/ducks/advertisementTemplate/reducers'
import _ from 'lodash'
import type { CarouselErrors, ControlsInfo } from './types'
import { mapProviderTemplatesDataToControlsData } from './connectProviderTemplatesWithControls'

type Errors = Array<string>

export type ErrorsResponseData = {|
  name?: Errors,
  audience_ids?: Errors,
  provider_templates?: Object
|}

export type ErrorsPack = {|
  commonErrors: CommonErrors,
  mediaErrors: MediaErrors,
  carouselErrors: CarouselErrors
|}

export function processAdvertisementErrors(item: Object): ErrorsPack {
  const controlsErrors = collectControlsErrors(item)
  const commonErrors = processProviderTemplatesCommonErrors(controlsErrors)
  const mediaErrors = processProviderTemplatesMediaErrors(controlsErrors)
  const carouselErrors = processCarouselErrors(controlsErrors)

  return { commonErrors, mediaErrors, carouselErrors }
}

function collectControlsErrors(providerTemplates: Object) {
  return mapProviderTemplatesDataToControlsData(providerTemplates, [], (a, b) => {
    return [...a, b]
  })
}

function processProviderTemplatesCommonErrors(
  controlsErrors: ControlsInfo<Array<any>>
): CommonErrors {
  const errorsToJoin: CommonErrorsInfo<Array<Array<string>>> = {
    columnDescriptionErrors: controlsErrors.columnDescription,
    fbLinkButtonErrors: controlsErrors.fbLinkButton,
    fbMultiDescriptionErrors: controlsErrors.fbMultiDescription,
    fbTitleErrors: controlsErrors.fbTitle,
    fbUrlErrors: controlsErrors.fbUrl,
    logoImageErrors: controlsErrors.logoImage,
    multiDescriptionErrors: controlsErrors.multiDescription,
    productDescriptionErrors: controlsErrors.productDescription,
    productTitleErrors: controlsErrors.productTitle,
    snippetImageErrors: controlsErrors.snippetImage,
    titleErrors: controlsErrors.title,
    tmCarouselDescriptionErrors: controlsErrors.tmCarouselDescription,
    tmLinkButtonErrors: controlsErrors.tmLinkButton,
    tmUrlErrors: controlsErrors.tmUrl,
    urlErrors: controlsErrors.url,
    vkLinkButtonErrors: controlsErrors.vkLinkButton,
    vkLinkTitleErrors: controlsErrors.vkLinkTitle,
    vkMultiDescriptionErrors: controlsErrors.vkMultiDescription,
    vkTitleErrors: controlsErrors.vkTitle,
    vkUrlErrors: controlsErrors.vkUrl,
    vkWithoutButtonDescriptionErrors: controlsErrors.vkWithoutButtonDescription
  }
  return _.fromPairs(_.map(errorsToJoin, (errors, key) => [key, _.uniq(_.flatten(errors))]))
}

export function processAdvertisementTemplateCommonErrors(
  errors: ErrorsResponseData
): {|
  commonErrors: CommonErrors,
  advertisementTemplateErrors: AdvertisementTemplateErrors,
  carouselErrors: CarouselErrors
|} {
  try {
    const providerTemplates = errors.provider_templates

    return {
      commonErrors: providerTemplates
        ? processProviderTemplatesCommonErrors(collectControlsErrors(providerTemplates))
        : emptyCommonErrors,
      advertisementTemplateErrors: {
        globalErrors: [],
        nameErrors: errors.name || []
      },
      carouselErrors: providerTemplates
        ? processCarouselErrors(collectControlsErrors(providerTemplates))
        : []
    }
  } catch (e) {
    return {
      carouselErrors: [],
      commonErrors: emptyCommonErrors,
      advertisementTemplateErrors: {
        globalErrors: [`Exception: ${e.toString()}`, `Response: ${JSON.stringify(errors)}`],
        nameErrors: []
      }
    }
  }
}

export function processAdvertisementTemplateMediaErrors(providerTemplates: Object): MediaErrors {
  const controlsErrors = collectControlsErrors(providerTemplates)

  return processProviderTemplatesMediaErrors(controlsErrors)
}

function processProviderTemplatesMediaErrors(
  controlsErrors: ControlsInfo<Array<any>>
): MediaErrors {
  return _.fromPairs(
    _.map(
      {
        columnImageErrors: controlsErrors.columnImage,
        fbInstagramImageErrors: controlsErrors.fbInstagramImage,
        fbStoriesImageErrors: controlsErrors.fbStoriesImage,
        fbThumbnailImageErrors: controlsErrors.fbThumbnailImage,
        multiImageErrors: controlsErrors.multiImage,
        tmSmallImageErrors: controlsErrors.tmSmallImage,
        videoErrors: controlsErrors.video,
        vkBigImageErrors: controlsErrors.vkBigImage
      },
      (errors, key) => [key, _.uniq(_.flatten(errors))]
    )
  )
}

function processCarouselErrors(controlsErrors: ControlsInfo<Array<any>>) {
  const providerCarousels = [
    controlsErrors.vkCarousel[0] || [],
    controlsErrors.tmCarousel[0] || [],
    controlsErrors.fbCarousel[0] || []
  ]

  const stringErrors = _.find(
    providerCarousels,
    (errors) => _.isArray(errors) && typeof errors[0] === 'string'
  )
  if (stringErrors) return stringErrors

  // merge cards errors
  const cardsErrors = []
  _.each(providerCarousels, (errors) => {
    if (_.isPlainObject(errors) && errors.cards) {
      _.each(errors.cards, (cardErrors, index: number) => {
        while (cardsErrors.length <= index) cardsErrors.push({})
        const currentErrors = cardsErrors[index]
        if (typeof currentErrors === 'string') return
        if (typeof cardErrors === 'string') {
          cardsErrors[index] = cardErrors
        } else {
          cardsErrors[index] = { ...currentErrors, ...cardErrors }
        }
      })
    }
  })
  if (!_.isEmpty(cardsErrors)) return { cards: cardsErrors }

  return []
}
